import { useMemo } from 'react';
import { eventTabsConfig, EventTabConfig } from 'src/routes';
import { usePermissions } from 'src/hooks/usePermissions';
import { useViewOnlySecret } from 'src/hooks/useViewOnlySecret';

const VIEW_ONLY_ALLOWED_TAB_IDS = ['info', 'map', 'notes', 'todo'];

export function useFilteredEventTabs(eventId: string | undefined): Array<{ tab: EventTabConfig; index: number }> {
    const { viewOnly, secret } = useViewOnlySecret();
    const viewOnlyWithSecret = viewOnly && secret != null;
    const {
        isLoading,
        canAccessEvent,
        canViewMap,
        canViewInventory,
        canViewWasteData,
        canEditUsers,
        canEditTodos,
        canCompleteTodos,
        canEditEvents,
    } = usePermissions();

    return useMemo(() => {
        if (!eventId || isLoading) return [];

        if (viewOnlyWithSecret) {
            return eventTabsConfig.filter(tab => VIEW_ONLY_ALLOWED_TAB_IDS.includes(tab.id)).map((tab, index) => ({ tab, index }));
        }

        if (!viewOnly && !canAccessEvent(eventId)) return [];

        return eventTabsConfig
            .map((tab, index) => ({ tab, index }))
            .filter(({ tab }) => {
                if (viewOnly && tab.privateOnly) return false;
                switch (tab.id) {
                    case 'info':
                    case 'map':
                    case 'notes':
                        return canViewMap;
                    case 'inventory':
                        return canViewInventory;
                    case 'todo':
                        return canEditTodos || canCompleteTodos;
                    case 'data':
                        return canViewWasteData;
                    case 'settings':
                        return canEditEvents;
                    case 'eventUsers':
                        return canEditUsers;
                    default:
                        return false;
                }
            });
    }, [
        eventId,
        isLoading,
        viewOnly,
        viewOnlyWithSecret,
        canAccessEvent,
        canViewMap,
        canViewInventory,
        canEditTodos,
        canCompleteTodos,
        canViewWasteData,
        canEditEvents,
        canEditUsers,
    ]);
}
